import {FIELD_TYPE, FIELD_TYPES, TEXT_FIELD_TYPES} from '../../core/vocabulary.js';
import {escapeHtml} from '../../core/escape-html.js';
import {readTableConfig, tableEditorMarkup} from './table-column-editor.view.js';

/** @typedef {import('../../core/client/template-fields.core.js').FieldConfig} FieldConfig */

const FIELD_TYPE_LABELS = Object.freeze({
    [FIELD_TYPE.TEXT]: 'Text',
    [FIELD_TYPE.TEXTAREA]: 'Long Text',
    [FIELD_TYPE.IMAGE]: 'Image',
    [FIELD_TYPE.NUMBER]: 'Number',
    [FIELD_TYPE.CHECKBOX]: 'Checkbox',
    [FIELD_TYPE.DOCUMENT]: 'Document',
    [FIELD_TYPE.TABLE]: 'Table',
    [FIELD_TYPE.SELECT]: 'Dropdown',
});

/**
 * @param {string} key
 * @param {FieldConfig} config
 * @returns {string} markup of one `.field-row` in the template field list
 */
export function fieldRowMarkup(key, config) {
    return `
            <div class="field-row" data-key="${escapeHtml(key)}" data-manual="${config.manual ? 'true' : 'false'}">
                <div class="field-meta"><strong>{{${escapeHtml(key)}}}</strong></div>
                <div class="field-inputs">
                    <input type="text" value="${escapeHtml(config.label)}" data-prop="label" placeholder="Label" required
                        aria-label="Label for ${escapeHtml(key)}" />
                    <select data-prop="type" aria-label="Field type for ${escapeHtml(key)}">
                        ${FIELD_TYPES.map((type) => `<option value="${type}" ${config.type === type ? 'selected' : ''}>${FIELD_TYPE_LABELS[type]}</option>`).join('')}
                    </select>
                    <input type="text" data-prop="editorGroup" maxlength="120"
                        placeholder="Editor section (optional, e.g. Introduction)"
                        aria-label="Editor section for ${escapeHtml(key)}"
                        value="${escapeHtml(config.editorGroup ?? '')}">
                    <input type="text" data-prop="help" maxlength="300"
                        placeholder="Help text under the label (optional)"
                        aria-label="Help text for ${escapeHtml(key)}"
                        value="${escapeHtml(config.help ?? '')}">
                    <input type="number" data-prop="recommendedLength" min="1" max="100000" step="1"
                        placeholder="Recommended length (optional)"
                        title="Characters the page editor's counter measures against; a guide, never enforced"
                        aria-label="Recommended length in characters for ${escapeHtml(key)}"
                        value="${escapeHtml(config.recommendedLength ?? '')}"
                        ${TEXT_FIELD_TYPES.includes(config.type) ? '' : 'hidden disabled'}>
                    <label class="check-req">
                        <input type="checkbox" data-prop="required" ${config.required ? 'checked' : ''}> Required
                    </label>
                    <button type="button" class="btn btn-outline btn-sm btn-remove-field" data-key="${escapeHtml(key)}"
                        aria-label="Remove field ${escapeHtml(key)}">
                        Remove
                    </button>
                </div>
                <div class="field-references">
                    <input type="text" data-prop="dependsOn"
                        placeholder="Shown only when this field is filled (optional)"
                        aria-label="Field that shows ${escapeHtml(key)}"
                        value="${escapeHtml(config.dependsOn ?? '')}">
                </div>
                <textarea class="field-select-options" data-prop="options" rows="3"
                    placeholder="One option per line" aria-label="Options for ${escapeHtml(key)}"
                    ${config.type === FIELD_TYPE.SELECT ? 'required' : 'hidden'}>${escapeHtml(config.options ?? '')}</textarea>
                <div class="table-editor" ${config.type !== FIELD_TYPE.TABLE ? 'hidden' : ''}>${tableEditorMarkup(config)}</div>
            </div>
        `;
}

/**
 * Reads the current inputs of a field row, so typed values survive a re-render of the list.
 *
 * @param {HTMLElement} row
 * @param {FieldConfig | undefined} existing the stored state of the row, if any
 * @returns {FieldConfig}
 */
export function readFieldRow(row, existing) {
    const type = row.querySelector('[data-prop="type"]').value;
    return {
        label: row.querySelector('[data-prop="label"]').value,
        type,
        required: row.querySelector('[data-prop="required"]').checked,
        manual: existing?.manual ?? row.dataset.manual === 'true',
        editorGroup: row.querySelector('[data-prop="editorGroup"]')?.value?.trim() || null,
        help: row.querySelector('[data-prop="help"]')?.value?.trim() || null,
        recommendedLength: row.querySelector('[data-prop="recommendedLength"]').value.trim() || null,
        dependsOn: row.querySelector('[data-prop="dependsOn"]')?.value?.trim() || null,
        options: row.querySelector('[data-prop="options"]')?.value ?? existing?.options ?? '',
        ...readTableConfig(row, existing)
    };
}

/**
 * Shows only the inputs that apply to the selected field type.
 *
 * @param {HTMLElement} row
 * @param {string} type
 */
export function syncFieldRowTypeControls(row, type) {
    const recommendedLengthInput = row.querySelector('[data-prop="recommendedLength"]');
    recommendedLengthInput.hidden = !TEXT_FIELD_TYPES.includes(type);
    recommendedLengthInput.disabled = !TEXT_FIELD_TYPES.includes(type);
    const optionsInput = row.querySelector('[data-prop="options"]');
    optionsInput.hidden = type !== FIELD_TYPE.SELECT;
    optionsInput.required = type === FIELD_TYPE.SELECT;
}
